import React, { useEffect, useState } from 'react';
import { adminApiService } from '../../services/adminApiService';
import { productService } from '../../services/productService';
import { FileTooltip } from '../common/FileTooltip';
import { FilePreviewModal } from '../common/FilePreviewModal';

interface ReviewUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  userType?: string;
  riskCategory?: string;
  reviewRequired?: boolean;
  sepaDailyLimit?: number;
  sepaMonthlyLimit?: number;
  sepaYearlyLimit?: number;
  sepaSingleTransactionLimit?: number;
  sepaLimitCurrency?: string;
  companyName?: string;
  registrationDate?: string;
  industry?: string;
  kycStatus?: string;
  productId?: number;
  productType?: string;
  productName?: string;
  // --- Compliance/KYC details ---
  address?: string;
  phone?: string;
  kycLevel?: string;
  eddStatus?: string;
  complianceNotes?: string;
  documentLinks?: { 
    type: string; 
    url: string; 
    isSubmitted: boolean;
    fileName?: string;
    uploadDate?: string;
    fileSize?: number;
    contentType?: string;
  }[];
}

const riskCategories = ["ALL", "NORMAL", "HIGH_RISK"];
const userTypes = ["ALL", "BUSINESS_NORMAL", "BUSINESS_HIGH_RISK"];
const productTypes = ["ALL", "BANKING", "INVESTMENT"];

const ReviewQueue: React.FC = () => {
  const [previewFile, setPreviewFile] = useState<{
    isOpen: boolean;
    url: string;
    fileName: string;
    contentType?: string;
  }>({
    isOpen: false,
    url: '',
    fileName: '',
    contentType: undefined
  });

  const [users, setUsers] = useState<ReviewUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Product filtering state
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>(undefined);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filterRisk, setFilterRisk] = useState<string>('ALL');
  const [filterType, setFilterType] = useState<string>('ALL');
  const [filterProduct, setFilterProduct] = useState<string>('ALL');
  const [search, setSearch] = useState<string>('');
  const [detailsUser, setDetailsUser] = useState<ReviewUser | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const renderDocumentCell = (user: ReviewUser, docType: string) => {
    const doc = user.documentLinks?.find(d => d.type === docType);
    if (!doc?.isSubmitted) return <span className="doc-pill doc-pill--gray" title="Missing">—</span>;

    const isImage = (doc.contentType || '').startsWith('image/');

    if (isImage) {
      return (
        <FileTooltip
          fileName={doc.fileName || doc.url.split('/').pop() || 'Unknown'}
          uploadDate={doc.uploadDate}
          fileSize={doc.fileSize}
        >
          <button
            className="doc-thumb"
            onClick={() => setPreviewFile({
              isOpen: true,
              url: doc.url,
              fileName: doc.fileName || doc.url.split('/').pop() || docType,
              contentType: doc.contentType
            })}
            aria-label={`View ${docType}`}
            title={`View ${docType}`}
          >
            <img src={doc.url} alt={docType} />
            <span className="doc-thumb-overlay">🔍</span>
          </button>
        </FileTooltip>
      );
    }

    const pillClass = docType === 'memorandumOfAssociation' || docType === 'articlesOfAssociation' ? 'doc-pill--purple' : 'doc-pill--blue';
    const emoji = docType === 'certificateOfIncorporation' ? '📜' : '📄';

    return (
      <FileTooltip
        fileName={doc.fileName || doc.url.split('/').pop() || 'Unknown'}
        uploadDate={doc.uploadDate}
        fileSize={doc.fileSize}
      >
        <button
          className={`doc-pill ${pillClass}`}
          onClick={() => setPreviewFile({
            isOpen: true,
            url: doc.url,
            fileName: doc.fileName || doc.url.split('/').pop() || docType,
            contentType: doc.contentType
          })}
          aria-label={`View ${docType}`}
          title={`View ${docType}`}
        >
          <span className="doc-emoji">{emoji}</span>
        </button>
      </FileTooltip>
    );
  };

  // Load products on component mount
  useEffect(() => {
    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        console.log('Loading products for ReviewQueue filter...');
        
        // Get current admin user profile to check assigned products
        const profileResponse = await adminApiService.getAdminProfile();
        console.log('Admin profile response:', profileResponse);
        console.log('Admin profile response data:', JSON.stringify(profileResponse.data, null, 2));
        
        if (profileResponse.success && profileResponse.data) {
          const adminUser = profileResponse.data;
          console.log('Current admin user:', adminUser);
          console.log('Admin user role:', adminUser.role);
          console.log('Admin user assignedProducts:', adminUser.assignedProducts);
          console.log('Assigned products type:', typeof adminUser.assignedProducts);
          console.log('Assigned products length:', adminUser.assignedProducts?.length);
          
          // If user has assigned products, filter to only show BUSINESS products (KYB)
          if (adminUser.assignedProducts && adminUser.assignedProducts.length > 0) {
            console.log('Admin has assigned products:', adminUser.assignedProducts);
            const businessProducts = adminUser.assignedProducts.filter((product: any) => 
              product.customerType === 'BUSINESS'
            );
            setProducts(businessProducts);
          } else if (adminUser.role === 'SUPER_ADMIN') {
            // SUPER_ADMIN can see all business products (KYB)
            console.log('SUPER_ADMIN - loading business products for KYB');
            const businessResponse = await productService.getBusinessProducts();
            setProducts(businessResponse.success && businessResponse.data ? businessResponse.data : []);
          } else {
            // Other roles see no products
            console.log('No assigned products for this admin user');
            setProducts([]);
          }
        } else {
          console.error('Failed to get admin profile');
          setProducts([]);
        }
      } catch (error) {
        console.error('Failed to load products:', error);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };
    
    loadProducts();
  }, []);

  useEffect(() => {
    loadReviewUsers();
    // eslint-disable-next-line
  }, [selectedProductId]);

  // Reset to first page when filters/search change
  useEffect(() => { setPage(1); }, [filterRisk, filterType, filterProduct, search, users.length]);

  const loadReviewUsers = async () => {
    console.log('=== KYB Review Queue Debug ===');
    console.log('Loading review users...');
    setLoading(true);
    setError(null);
    try {
      console.log('Calling getKybSubmissions with productId:', selectedProductId);
      const resp = await adminApiService.getKybSubmissions(0, 100, undefined, selectedProductId);
      console.log('KYB API Response:', resp);
      if (resp.success && resp.data) {
        const firstSubmission = resp.data.content[0];
        console.log('First KYB Submission:', {
          id: firstSubmission?.id,
          documents: firstSubmission?.documents,
          paths: {
            certificateOfIncorporation: firstSubmission?.certificateOfIncorporationPath,
            memorandum: firstSubmission?.memorandumPath,
            articles: firstSubmission?.articlesPath,
            boardResolution: firstSubmission?.boardResolutionPath,
            beneficialOwnership: firstSubmission?.beneficialOwnershipPath,
            financialStatements: firstSubmission?.financialStatementsPath,
            businessPlan: firstSubmission?.businessPlanPath,
            other: firstSubmission?.otherDocumentsPath
          }
        });
        console.log('Document flags:', firstSubmission?.documents);
        const kybUsers = resp.data.content.map((submission: any) => ({
          id: submission.id.toString(),
          username: submission.userName,
          email: submission.email,
          firstName: submission.companyName,
          lastName: '',
          userType: 'BUSINESS_NORMAL',
          riskCategory: 'NORMAL',
          reviewRequired: true,
          companyName: submission.companyName,
          registrationDate: submission.submittedAt,
          industry: submission.industry,
          documentLinks: (() => {
            const docs = {
              id: submission.id,
              certificateOfIncorporationPath: submission.certificateOfIncorporationPath,
              certificateOfIncorporationDoc: submission.documents?.CERTIFICATE_OF_INCORPORATION,
              documents: submission.documents
            };
            console.log('Mapping documents for submission:', docs);
            const links = [
              { 
                type: 'certificateOfIncorporation', 
                url: submission.certificateOfIncorporationPath || submission.documents?.CERTIFICATE_OF_INCORPORATION, 
                isSubmitted: !!(submission.certificateOfIncorporationPath || submission.documents?.CERTIFICATE_OF_INCORPORATION),
                fileName: submission.certificateOfIncorporationPath?.split('/').pop() || 'Certificate of Incorporation',
                uploadDate: submission.uploadDate,
                fileSize: submission.fileSize,
                contentType: submission.contentType || 'application/pdf'
              },
              { 
                type: 'memorandumOfAssociation', 
                url: submission.memorandumPath || submission.documents?.MEMORANDUM_OF_ASSOCIATION,
                isSubmitted: !!(submission.memorandumPath || submission.documents?.MEMORANDUM_OF_ASSOCIATION),
                fileName: submission.memorandumPath?.split('/').pop() || 'Memorandum of Association',
                uploadDate: submission.uploadDate,
                fileSize: submission.fileSize,
                contentType: submission.contentType || 'application/pdf'
              },
              { 
                type: 'articlesOfAssociation', 
                url: submission.articlesPath || submission.documents?.ARTICLES_OF_ASSOCIATION,
                isSubmitted: !!(submission.articlesPath || submission.documents?.ARTICLES_OF_ASSOCIATION),
                fileName: submission.articlesPath?.split('/').pop() || 'Articles of Association',
                uploadDate: submission.uploadDate,
                fileSize: submission.fileSize,
                contentType: submission.contentType || 'application/pdf'
              },
              { 
                type: 'boardResolution', 
                url: submission.boardResolutionPath || submission.documents?.BOARD_RESOLUTION,
                isSubmitted: !!(submission.boardResolutionPath || submission.documents?.BOARD_RESOLUTION),
                fileName: submission.boardResolutionPath?.split('/').pop() || 'Board Resolution',
                uploadDate: submission.uploadDate,
                fileSize: submission.fileSize,
                contentType: submission.contentType || 'application/pdf'
              },
              { 
                type: 'beneficialOwnership', 
                url: submission.beneficialOwnershipPath || submission.documents?.BENEFICIAL_OWNERSHIP,
                isSubmitted: !!(submission.beneficialOwnershipPath || submission.documents?.BENEFICIAL_OWNERSHIP),
                fileName: submission.beneficialOwnershipPath?.split('/').pop() || 'Beneficial Ownership',
                uploadDate: submission.uploadDate,
                fileSize: submission.fileSize,
                contentType: submission.contentType || 'application/pdf'
              },
              { 
                type: 'financialStatements', 
                url: submission.financialStatementsPath || submission.documents?.FINANCIAL_STATEMENTS,
                isSubmitted: !!(submission.financialStatementsPath || submission.documents?.FINANCIAL_STATEMENTS),
                fileName: submission.financialStatementsPath?.split('/').pop() || 'Financial Statements',
                uploadDate: submission.uploadDate,
                fileSize: submission.fileSize,
                contentType: submission.contentType || 'application/pdf'
              },
              { 
                type: 'businessPlan', 
                url: submission.businessPlanPath || submission.documents?.BUSINESS_PLAN,
                isSubmitted: !!(submission.businessPlanPath || submission.documents?.BUSINESS_PLAN),
                fileName: submission.businessPlanPath?.split('/').pop() || 'Business Plan',
                uploadDate: submission.uploadDate,
                fileSize: submission.fileSize,
                contentType: submission.contentType || 'application/pdf'
              },
              { 
                type: 'other', 
                url: submission.otherDocumentsPath || submission.documents?.OTHER,
                isSubmitted: !!(submission.otherDocumentsPath || submission.documents?.OTHER),
                fileName: submission.otherDocumentsPath?.split('/').pop() || 'Other Documents',
                uploadDate: submission.uploadDate,
                fileSize: submission.fileSize,
                contentType: submission.contentType || 'application/pdf'
              }
            ];
            console.log('Document links:', links);
            return links;
          })()
        }));
        setUsers(kybUsers);
      } else {
        setError(resp.error || 'Failed to load review queue');
      }
    } catch (err) {
      setError('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  // Filtering logic
  const filteredUsers = users.filter(user => {
    if (filterRisk !== 'ALL' && user.riskCategory !== filterRisk) return false;
    if (filterType !== 'ALL' && user.userType !== filterType) return false;
    if (filterProduct !== 'ALL' && user.productType !== filterProduct) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(
        user.firstName?.toLowerCase().includes(s) ||
        user.lastName?.toLowerCase().includes(s) ||
        user.username?.toLowerCase().includes(s) ||
        user.email?.toLowerCase().includes(s) ||
        user.productName?.toLowerCase().includes(s)
      )) return false;
    }
    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const pagedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  // Export CSV
  const handleExportCSV = () => {
    const fields = [
      'id','firstName','lastName','username','email','userType','riskCategory','companyName','registrationDate','industry','kycStatus','address','phone','kycLevel','eddStatus','complianceNotes','sepaDailyLimit','sepaMonthlyLimit','sepaYearlyLimit','sepaSingleTransactionLimit','sepaLimitCurrency'
    ];
    const header = fields.join(',');
    const rows = filteredUsers.map(u =>
      fields.map(f => {
        let val = (u as any)[f];
        if (val === undefined || val === null) return '';
        if (typeof val === 'string') return '"' + val.replace(/"/g, '""') + '"';
        return val;
      }).join(',')
    );
    const csv = [header, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'review-queue.csv';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  const handleApprove = async (user: ReviewUser) => {
    setActionLoading(user.id);
    try {
      const resp = await adminApiService.post(`/admin/kyb-submissions/${user.id}/approve`, {
        status: 'APPROVED',
        comments: 'Approved by admin'
      });
      if (resp.success) {
        alert('KYB submission approved.');
        await loadReviewUsers();
        setSelected(new Set());
      } else {
        alert(resp.error || 'Failed to approve KYB submission');
      }
    } catch (err) {
      alert('Failed to approve KYB submission');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (user: ReviewUser) => {
    setActionLoading(user.id);
    try {
      const resp = await adminApiService.post(`/admin/kyb-submissions/${user.id}/reject`, {
        status: 'REJECTED',
        comments: 'Rejected by admin - High Risk'
      });
      if (resp.success) {
        alert('KYB submission rejected.');
        await loadReviewUsers();
        setSelected(new Set());
      } else {
        alert(resp.error || 'Failed to reject KYB submission');
      }
    } catch (err) {
      alert('Failed to reject KYB submission');
    } finally {
      setActionLoading(null);
    }
  };

  // Bulk actions
  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setActionLoading('bulk');
    try {
      for (const id of Array.from(selected)) {
        await adminApiService.post(`/admin/kyb-submissions/${id}/approve`, {
          status: 'APPROVED',
          comments: 'Bulk approved by admin'
        });
      }
      alert('Selected KYB submissions approved.');
      await loadReviewUsers();
      setSelected(new Set());
    } catch (err) {
      alert('Failed to approve selected KYB submissions');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkReject = async () => {
    if (selected.size === 0) return;
    setActionLoading('bulk');
    try {
      for (const id of Array.from(selected)) {
        await adminApiService.post(`/admin/kyb-submissions/${id}/reject`, {
          status: 'REJECTED',
          comments: 'Bulk rejected by admin - High Risk'
        });
      }
      alert('Selected KYB submissions rejected.');
      await loadReviewUsers();
      setSelected(new Set());
    } catch (err) {
      alert('Failed to reject selected KYB submissions');
    } finally {
      setActionLoading(null);
    }
  };

  // Selection logic
  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(filteredUsers.map(u => u.id)));
  };

  const deselectAll = () => {
    setSelected(new Set());
  };

  const allSelected = filteredUsers.length > 0 && filteredUsers.every(u => selected.has(u.id));

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center' }}>Loading review queue...</div>;
  }

  if (error) {
    return <div style={{ padding: 32, color: 'red', textAlign: 'center' }}>{error}</div>;
  }

  if (filteredUsers.length === 0) {
    return <div style={{ padding: 32, textAlign: 'center' }}>No users currently require review.</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <h3 style={{ marginBottom: 24 }}>Flagged Business Registrations (Review Queue)</h3>
      <div style={{ marginBottom: 12 }}>
        <button onClick={handleExportCSV} style={{ padding: '6px 16px', borderRadius: 4, border: 'none', background: '#6366f1', color: 'white', cursor: 'pointer' }}>
          Export CSV
        </button>
      </div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <select value={filterRisk} onChange={e => setFilterRisk(e.target.value)} style={{ padding: 6 }}>
          {riskCategories.map(r => <option key={r} value={r}>{r === 'ALL' ? 'All Risks' : r}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: 6 }}>
          {userTypes.map(t => <option key={t} value={t}>{t === 'ALL' ? 'All Types' : t}</option>)}
        </select>
        <select 
          value={selectedProductId || ''} 
          onChange={e => setSelectedProductId(e.target.value ? Number(e.target.value) : undefined)} 
          style={{ padding: 6 }}
          disabled={productsLoading}
        >
          <option value="">All Products</option>
          {productsLoading && <option disabled>Loading products...</option>}
          {products.length === 0 && !productsLoading && <option disabled>No products available</option>}
          {products.map(product => (
            <option key={product.id} value={product.id}>
              {product.name} ({product.type})
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search name, email, username..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: 6, flex: 1 }}
        />
        <button onClick={selectAll} style={{ padding: 6 }}>Select All</button>
        <button onClick={deselectAll} style={{ padding: 6 }}>Deselect All</button>
      </div>
      {/* Bulk actions */}
      <div style={{ marginBottom: 12 }}>
        <button
          onClick={handleBulkApprove}
          disabled={selected.size === 0 || actionLoading === 'bulk'}
          style={{ marginRight: 8, padding: '6px 16px', borderRadius: 4, border: 'none', background: '#10b981', color: 'white', cursor: 'pointer' }}
        >
          {actionLoading === 'bulk' ? 'Processing...' : 'Bulk Approve'}
        </button>
        <button
          onClick={handleBulkReject}
          disabled={selected.size === 0 || actionLoading === 'bulk'}
          style={{ padding: '6px 16px', borderRadius: 4, border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer' }}
        >
          {actionLoading === 'bulk' ? 'Processing...' : 'Bulk Reject'}
        </button>
      </div>
      {/* Pagination controls */}
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: '4px 12px' }}>Prev</button>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => setPage(i + 1)}
            style={{
              padding: '4px 10px',
              background: page === i + 1 ? '#3b82f6' : '#f3f4f6',
              color: page === i + 1 ? 'white' : '#111827',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: page === i + 1 ? 600 : 400
            }}
          >
            {i + 1}
          </button>
        ))}
        <button disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: '4px 12px' }}>Next</button>
        <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 13 }}>
          Page {page} of {totalPages}
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
        <thead style={{ background: '#f9fafb' }}>
          <tr>
            <th style={{ padding: 12 }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={e => e.target.checked ? selectAll() : deselectAll()}
              />
            </th>
            <th style={{ padding: 12, textAlign: 'left' }}>Name</th>
            <th style={{ padding: 12, textAlign: 'left' }}>Username</th>
            <th style={{ padding: 12, textAlign: 'left' }}>Email</th>
            <th style={{ padding: 12, textAlign: 'left' }}>User Type</th>
            <th style={{ padding: 12, textAlign: 'left' }}>Risk Category</th>
            <th style={{ padding: 12, textAlign: 'left' }}>Product</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Certificate of Incorporation</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Memorandum</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Articles</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Board Resolution</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Beneficial Ownership</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Financial Statements</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Business Plan</th>
            <th style={{ padding: 12, textAlign: 'left' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pagedUsers.map(user => (
            <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: 12 }}>
                <input
                  type="checkbox"
                  checked={selected.has(user.id)}
                  onChange={() => toggleSelect(user.id)}
                />
              </td>
              <td style={{ padding: 12 }}>{user.firstName} {user.lastName}</td>
              <td style={{ padding: 12 }}>{user.username}</td>
              <td style={{ padding: 12 }}>{user.email}</td>
              <td style={{ padding: 12 }}>{user.userType}</td>
              <td style={{ padding: 12 }}>{user.riskCategory}</td>
              <td style={{ padding: 12 }}>{user.productName || user.productType || '-'}</td>
              <td style={{ padding: 12, textAlign: 'center' }}>
                {renderDocumentCell(user, 'certificateOfIncorporation')}
              </td>
              <td style={{ padding: 12, textAlign: 'center' }}>
                {renderDocumentCell(user, 'memorandumOfAssociation')}
              </td>
              <td style={{ padding: 12, textAlign: 'center' }}>
                {renderDocumentCell(user, 'articlesOfAssociation')}
              </td>
              <td style={{ padding: 12, textAlign: 'center' }}>
                {renderDocumentCell(user, 'boardResolution')}
              </td>
              <td style={{ padding: 12, textAlign: 'center' }}>
                {renderDocumentCell(user, 'beneficialOwnership')}
              </td>
              <td style={{ padding: 12, textAlign: 'center' }}>
                {renderDocumentCell(user, 'financialStatements')}
              </td>
              <td style={{ padding: 12, textAlign: 'center' }}>
                {renderDocumentCell(user, 'businessPlan')}
              </td>
              <td style={{ padding: 12 }}>
                <button
                  onClick={() => handleApprove(user)}
                  disabled={actionLoading === user.id}
                  style={{ marginRight: 8, padding: '6px 12px', borderRadius: 4, border: 'none', background: '#10b981', color: 'white', cursor: 'pointer' }}
                >
                  {actionLoading === user.id ? 'Processing...' : 'Approve'}
                </button>
                <button
                  onClick={() => handleReject(user)}
                  disabled={actionLoading === user.id}
                  style={{ marginRight: 8, padding: '6px 12px', borderRadius: 4, border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer' }}
                >
                  {actionLoading === user.id ? 'Processing...' : 'Reject'}
                </button>
                <button
                  onClick={() => setDetailsUser(user)}
                  style={{ padding: '6px 12px', borderRadius: 4, border: 'none', background: '#3b82f6', color: 'white', cursor: 'pointer' }}
                >
                  Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Details Modal */}
      {detailsUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setDetailsUser(null)}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 32, minWidth: 400, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <h4>User Details</h4>
            <div><b>Name:</b> {detailsUser.firstName} {detailsUser.lastName}</div>
            <div><b>Username:</b> {detailsUser.username}</div>
            <div><b>Email:</b> {detailsUser.email}</div>
            <div><b>User Type:</b> {detailsUser.userType}</div>
            <div><b>Risk Category:</b> {detailsUser.riskCategory}</div>
            <div><b>Product:</b> {detailsUser.productName || detailsUser.productType || '-'}</div>
            <div><b>Company Name:</b> {detailsUser.companyName || '-'}</div>
            <div><b>Registration Date:</b> {detailsUser.registrationDate || '-'}</div>
            <div><b>Industry:</b> {detailsUser.industry || '-'}</div>
            <div><b>KYC Status:</b> {detailsUser.kycStatus || '-'}</div>
            <div><b>SEPA Daily Limit:</b> {detailsUser.sepaDailyLimit || '-'} {detailsUser.sepaLimitCurrency || ''}</div>
            <div><b>SEPA Monthly Limit:</b> {detailsUser.sepaMonthlyLimit || '-'} {detailsUser.sepaLimitCurrency || ''}</div>
            <div><b>SEPA Yearly Limit:</b> {detailsUser.sepaYearlyLimit || '-'} {detailsUser.sepaLimitCurrency || ''}</div>
            <div><b>SEPA Single Transaction Limit:</b> {detailsUser.sepaSingleTransactionLimit || '-'} {detailsUser.sepaLimitCurrency || ''}</div>
            {/* Compliance/KYC details */}
            <div><b>Address:</b> {detailsUser.address || '-'}</div>
            <div><b>Phone:</b> {detailsUser.phone || '-'}</div>
            <div><b>KYC Level:</b> {detailsUser.kycLevel || '-'}</div>
            <div><b>EDD Status:</b> {detailsUser.eddStatus || '-'}</div>
            <div><b>Compliance Notes:</b> {detailsUser.complianceNotes || '-'}</div>
            <div><b>Documents:</b> {detailsUser.documentLinks && detailsUser.documentLinks.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {detailsUser.documentLinks.map((doc, idx) => (
                  <li key={idx}>
                    <button
                      onClick={() => setPreviewFile({
                        isOpen: true,
                        url: doc.url,
                        fileName: doc.fileName || doc.url.split('/').pop() || doc.type,
                        contentType: doc.contentType
                      })}
                      style={{ 
                        color: '#3b82f6',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline'
                      }}
                    >
                      {doc.type}
                    </button>
                  </li>
                ))}
              </ul>
            ) : '-'}
            </div>
            <button style={{ marginTop: 24, padding: '8px 24px', borderRadius: 4, background: '#3b82f6', color: 'white', border: 'none', cursor: 'pointer' }} onClick={() => setDetailsUser(null)}>Close</button>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={previewFile.isOpen}
        onClose={() => setPreviewFile({ isOpen: false, url: '', fileName: '', contentType: undefined })}
        fileUrl={previewFile.url}
        fileName={previewFile.fileName}
        contentType={previewFile.contentType}
      />
    </div>
  );
};

export default ReviewQueue; 